import { Editable, Slate, withReact } from "slate-react";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { StatusType, TaskType } from "App.types";
import { withHistory } from "slate-history";
import { createEditor, Transforms } from "slate";
import { ignoreTags } from "App.constants";
import { Button, Popconfirm } from "antd";
import { getCompletedSlate, withCustomLogic } from "App.helpers";
import { SoundFilled } from "@ant-design/icons";
import SentenceMaskedLeaf from "./SentenceLeaf";
import styles from "./SentenceTask.module.scss";
import cx from "classnames";
import { SentenceTaskProps } from "./SentenceTask.type";
import { useAudioTranscript } from "./Helpers/useAudioTranscript";
import RecordButton from "Components/RecordButton";
import TaskPanel from "Components/TaskPanel";
import { useSpeechCheckRead } from "Hooks/useSpeechCheckRead";
import useSocketSpeech from "Hooks/useSocketSpeech";

const initialValue = [{ children: [{ text: "" }] }];

const RepeatTask: FC<SentenceTaskProps> = ({
  sentence,
  sentence: { text, tags = [], translate, id, storyId, markers, info, button },
  onTaskComplete,
  onNext,
  children,
  audio,
  setDictOpened,
  alignCenter = false,
  showGrammar,
  transcripts,
  lesson,
  noTranslate,
}) => {
  const [status, setStatus] = useState<StatusType>(StatusType.Editing);
  const [activeLeaf, setActiveLeaf] = useState<number | null>(null);

  const editor = useMemo(() => withReact(withHistory(withCustomLogic(createEditor()))), []);

  const {
    start,
    stop,
    results: speechResults,
    isSpeaking,
  } = useSocketSpeech({
    text,
    tags,
    interim_results: true,
    setStatus,
    easy: markers.includes("easySpeak"),
  });

  useEffect(() => {
    audio?.addEventListener("play", stop);

    return () => audio?.removeEventListener("play", stop);
  }, [audio, stop]);

  const setInitialState = useCallback(() => {
    editor.children.forEach(() => {
      Transforms.delete(editor, { at: [0] });
    });

    editor.children = [];
    Transforms.insertNodes(editor, [{ children: getCompletedSlate(tags, text) }]);
  }, [editor, tags, text]);

  // initial
  useEffect(() => {
    setActiveLeaf(null);
    setStatus(StatusType.Editing);
    setInitialState();
  }, [sentence.id, editor, setInitialState]);

  // audio transcript
  useAudioTranscript({ setActiveLeaf, sentence, audio, transcripts });

  const { completedTagIdx, reset: resetSpeech } = useSpeechCheckRead({ speechResults, sentence });

  useEffect(() => {
    if (completedTagIdx >= tags.length - 1) {
      stop();
      setStatus(StatusType.Completed);
      onTaskComplete();
    }
  }, [completedTagIdx, onTaskComplete, stop, tags.length]);

  const renderLeaf = useCallback(
    (props: any) => (
      <SentenceMaskedLeaf
        placeholder
        hidden={status !== StatusType.Completed && props.leaf.originalIdx >= completedTagIdx + 1}
        underlined={props.leaf.audioIdx === activeLeaf}
        wrong={status === "error" && props.leaf.idx === (activeLeaf ?? 0) + 1 && !ignoreTags.includes(props.leaf.text)}
        showErrors={!["editing", "completed", "loading", "isRecording", ""].includes(status)}
        {...props}
      />
    ),
    [status, completedTagIdx, activeLeaf],
  );

  const onRecordClick = () => {
    if (status === StatusType.IsRecording) {
      stop();
    } else {
      // setCompletedSpeech("");
      resetSpeech();
      setStatus(StatusType.Loading);
      start();
    }
  };

  const onSkip = useCallback(() => {
    onTaskComplete().then(() => !info && onNext());
  }, [info, onNext, onTaskComplete]);

  return (
    <div className={styles.sentenceTask}>
      <div className={cx(styles.content, { [styles.content_hasChildren]: !!children, [styles.content_hasChildren]: !!children })}>
        <div className={styles.children}>{children}</div>

        <div className={cx(styles.slate, { [styles.slate_alignCenter]: alignCenter })}>
          <div className={styles.slate_wrapper}>
            <Slate editor={editor} initialValue={initialValue}>
              <Editable
                className={styles.textArea}
                readOnly
                onKeyDown={() => setStatus(StatusType.Editing)}
                renderLeaf={renderLeaf}
                //renderElement={(props) => <StoryElement isActive={activeSent === props.element.id} play={play} {...props} />}
              />
            </Slate>
          </div>
          {(!noTranslate || status === StatusType.Completed) && <div className={styles.translate}>{translate}</div>}
        </div>

        <div className={styles.bottom}>
          <Button icon={<SoundFilled />} onClick={() => audio?.play()} />
        </div>
      </div>

      <TaskPanel
        lessonId={lesson.id}
        task={TaskType.Repeat}
        sentId={id}
        storyId={storyId}
        showGrammar={showGrammar}
        onNext={onNext}
        isCompleted={status === StatusType.Completed}
        setDictOpened={setDictOpened}
        audio={audio}
        nextButton={button}
      >
        <span className={styles.buttons}>
          <RecordButton
            onClick={onRecordClick}
            isRecording={status === "isRecording"}
            loading={status === "loading"}
            isSpeaking={isSpeaking}
          />
          <Popconfirm title={"Не можешь говорить?"} onConfirm={onSkip}>
            <Button className={styles.btn_skip} size={"small"} type={"text"}>
              skip
            </Button>
          </Popconfirm>
        </span>
      </TaskPanel>
    </div>
  );
};

export default RepeatTask;
